// app/formSlice.ts (continued)

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  CreateFormResponse,
  FetchFormListRequest,
  FetchFormListResponse,
  FormFields,
  GetFormParams,
} from "../types/formTypes";
import {
  createFormAPI,
  deleteForm,
  fetchFormList,
  getFormById,
  getSubmitSurveyId,
  updateFormAPI,
} from "../services/formServices";

interface FormState {
  loading: boolean;
  success: boolean;
  error: string | null;
  response: CreateFormResponse | null;
  formList: FormFields[]; // ✅ Add this
  total: number; // ✅ And this
}

const initialState: FormState = {
  loading: false,
  success: false,
  error: null,
  response: null,
  formList: [], // ✅ Added
  total: 0, // ✅ Added
};

export const createForm = createAsyncThunk<
  CreateFormResponse, // ✅ Return type
  FormData, // ✅ Argument
  { rejectValue: string } // ✅ Error type
>("form/createForm", async (formData, { rejectWithValue }) => {
  try {
    const response = await createFormAPI(formData);
    return response;
  } catch (error: any) {
    return rejectWithValue(error || "Failed to create project");
  }
});

export const fetchAllForms = createAsyncThunk<
  FetchFormListResponse,
  FetchFormListRequest,
  { rejectValue: string }
>("form/fetchAll", async (params, { rejectWithValue }) => {
  try {
    return await fetchFormList(params);
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to fetch form list");
  }
});
export const deleteFormById = createAsyncThunk<
  FetchFormListResponse,
  GetFormParams,
  { rejectValue: string }
>(
  "form/delete",
  async ({ survey_version_id, survey_id }, { rejectWithValue }) => {
    try {
      return await deleteForm(Number(survey_version_id), Number(survey_id));
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to delete form");
    }
  }
);

export const fetchFormById = createAsyncThunk<
  CreateFormResponse,
  GetFormParams,
  { rejectValue: string }
>(
  "form/fetchById",
  async ({ survey_version_id, survey_id }, { rejectWithValue }) => {
    try {
      return await getFormById(Number(survey_version_id), Number(survey_id));
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch form");
    }
  }
);
// update specific form
export const updateForm = createAsyncThunk<
  CreateFormResponse, // ✅ Return type
  FormData, // ✅ Argument
  { rejectValue: string } // ✅ Error type
>("form/updateForm", async (formData, { rejectWithValue }) => {
  try {
    const response = await updateFormAPI(formData);
    return response;
  } catch (error: any) {
    return rejectWithValue(error || "Failed to create project");
  }
});

// get submit survey by id
export const getSubmitSurveyById = createAsyncThunk<
  CreateFormResponse,
  number,
  { rejectValue: string }
>("survey/getSpecificSurvey", async (id, { rejectWithValue }) => {
  try {
    return await getSubmitSurveyId(id);
  } catch (err: any) {
    return rejectWithValue(err.message || "Failed to get submit survey");
  }
});

const formSlice = createSlice({
  name: "form",
  initialState,
  reducers: {
    clearFormState: (state) => {
      state.loading = false;
      state.success = false;
      state.error = null;
      state.response = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createForm.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createForm.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.response = action.payload;
      })
      .addCase(createForm.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload || "Form creation failed";
      })
      // update form api
      .addCase(updateForm.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(updateForm.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.response = action.payload;
      })
      .addCase(updateForm.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload || "Form update failed";
      })
      // fetch all form
      .addCase(fetchAllForms.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllForms.fulfilled, (state, action) => {
        state.loading = false;
        state.formList = action.payload.data.data;
        state.total = action.payload.data.total;
      })
      .addCase(fetchAllForms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch form list";
      })
      // delete form
      .addCase(deleteFormById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFormById.fulfilled, (state, action) => {
        const deletedId = action.meta.arg.survey_id;
        state.formList = state.formList.filter((form) => form.id !== deletedId);
        state.total = Math.max(0, state.total - 1); // ✅ update total safely
        state.loading = false;
      })

      .addCase(deleteFormById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to Delete role";
      })

      // get detail specific form
      .addCase(fetchFormById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFormById.fulfilled, (state, action) => {
        state.loading = false;
        state.response = action.payload;
      })
      .addCase(fetchFormById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch form";
      })

      // get submit survey detail
      .addCase(getSubmitSurveyById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSubmitSurveyById.fulfilled, (state, action) => {
        state.loading = false;
        state.response = action.payload;
      })
      .addCase(getSubmitSurveyById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch form";
      });
  },
});

export const { clearFormState } = formSlice.actions;
export default formSlice.reducer;
